import { Decoration, WidgetType } from "@codemirror/view";

import {
    isCommentOperation,
    isDeleteOperation,
    isInsertOperation,
} from "components/organisms/manuscript/overleaf/ranges/operations";

export class ChangeDeletedWidget extends WidgetType {
    constructor(change, highlightType = null) {
        super();
        this.change = change;
        this.highlightType = highlightType;
    }

    toDOM() {
        const widget = document.createElement("span");
        widget.classList.add("ol-cm-change");
        widget.classList.add("ol-cm-change-d");
        if (this.highlightType) {
            widget.classList.add(`ol-cm-change-d-${this.highlightType}`);
        }
        return widget;
    }

    eq(old) {
        return old.highlightType === this.highlightType;
    }
}

export function buildTrackedCommentDecorations({ trackedChanges, comments, toCodeMirror }) {
    if (trackedChanges.length === 0 && comments.length === 0) {
        return Decoration.none;
    }

    const decorations = [];

    for (const change of trackedChanges.asSorted()) {
        const from = toCodeMirror(change.range.pos);
        if (change.tracking.type === "insert") {
            const to = toCodeMirror(change.range.end);
            if (from < to) {
                decorations.push(
                    Decoration.mark({
                        class: "ol-cm-change ol-cm-change-i",
                        tracking: change.tracking,
                        rangeType: "trackedChange",
                        change,
                    }).range(from, to),
                );
            }
        } else {
            decorations.push(
                Decoration.widget({
                    widget: new ChangeDeletedWidget(),
                    side: 1,
                    rangeType: "trackedChange",
                    change,
                }).range(from),
            );
        }
    }

    for (const comment of comments) {
        if (!comment.resolved) {
            for (const range of comment.ranges) {
                decorations.push(
                    Decoration.mark({
                        class: "ol-cm-change ol-cm-change-c",
                        id: comment.id,
                        "data-review-thread-id": comment.id,
                        rangeType: "comment",
                        comment,
                    }).range(toCodeMirror(range.pos), toCodeMirror(range.end)),
                );
            }
        }
    }

    return Decoration.set(decorations, true);
}

export function buildChangeDecorations(data) {
    if (!data?.ranges) {
        return Decoration.none;
    }

    const changes = [...(data.ranges.changes || []), ...(data.ranges.comments || [])];
    const decorations = [];

    for (const change of changes) {
        try {
            decorations.push(...createChangeRange(change, data));
        } catch (error) {
            // Ignore invalid change positions. The source model may still be catching up.
        }
    }

    return Decoration.set(decorations, true);
}

export function updateDeleteWidgetHighlight(decorations, predicate, highlightType = null) {
    const widgetsToReplace = [];
    const cursor = decorations.iter();

    while (cursor.value) {
        const widget = cursor.value.spec?.widget;
        if (widget instanceof ChangeDeletedWidget && predicate(widget)) {
            widgetsToReplace.push(cursor.value.spec.widget);
        }
        cursor.next();
    }

    return decorations.update({
        sort: true,
        filter: (_from, _to, decoration) => !widgetsToReplace.includes(decoration.spec?.widget),
        add: widgetsToReplace.map(({ change }) =>
            Decoration.widget({
                widget: new ChangeDeletedWidget(change, highlightType),
                side: 1,
                opType: "d",
                id: change.id,
                metadata: change.metadata,
            }).range(change.op.p, change.op.p),
        ),
    });
}

export function buildHighlightDecorations(className, op) {
    if (isDeleteOperation(op)) {
        return Decoration.none;
    }

    const opFrom = op.p;
    const opLength = isInsertOperation(op) ? op.i.length : op.c.length;
    const opType = isInsertOperation(op) ? "i" : "c";

    if (opLength === 0) {
        return Decoration.none;
    }

    return Decoration.set(
        Decoration.mark({
            class: `${className} ${className}-${opType}`,
        }).range(opFrom, opFrom + opLength),
        true,
    );
}

export function createChangeRange(change, data) {
    const { id, metadata, op } = change;
    const from = op.p;

    if (isDeleteOperation(op)) {
        const changeWidget = Decoration.widget({
            widget: new ChangeDeletedWidget(change),
            side: 1,
            opType: "d",
            id,
            metadata,
        });

        return [changeWidget.range(from, from)];
    }

    const commentOperation = isCommentOperation(op);
    if (commentOperation) {
        const thread = data?.threads?.[op.t];
        if (!thread || thread.resolved) {
            return [];
        }
    }

    const opType = commentOperation ? "c" : "i";
    const changedText = commentOperation ? op.c : op.i;
    const to = from + changedText.length;

    if (from === to) {
        return [];
    }

    const changeMark = Decoration.mark({
        tagName: "span",
        class: `ol-cm-change ol-cm-change-${opType}`,
        opType,
        id,
        ...(commentOperation ? { "data-review-thread-id": id } : {}),
        metadata,
    });

    return [changeMark.range(from, to)];
}
